import { handleApiError, handleApiErrorWithSchema, VerityApiClient } from "..";
import { ApiClient, ApiClientConfig } from "../api-client";
import { ApiClientError } from "../errors";
import { TypeFromSchemaValidator } from "../schema-validator";
import { ApiClientProctoringOptionV2, InstituteRule } from "../types";
import schemaValidators from "./schema-validators";

type ThirdPartyPlatform = {
	platformName: string;
	platformId: number;
};

type ThirdPartyPlatformRosters = {
	unselectedPlatformRoster: Map<number, ThirdPartyPlatform>;
	selectedPlatformRoster: Map<number, ThirdPartyPlatform>;
};

type JSONValue = string | number | boolean | null | undefined | JSONValue[] | { [key: string]: JSONValue };

export default class InstituteClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClientConfig) {
		super(config, schemaValidators);
	}

	get = async (instituteId: number) => {
		try {
			if (!Number.isInteger(instituteId)) {
				throw new Error(`Invalid instituteId: ${instituteId}`);
			}
			const response = await super.fetch(new URL(`/api/verity/institute/${instituteId}`, this.config.verityOrigin), {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.instituteInfo.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	isIntegrated = async (hostname: string) => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/institute/hostname/${hostname}`, this.config.verityOrigin),
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.integrated.validate(obj)) {
					return obj.integrated;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getLanguage = async () => {
		try {
			const response = await super.fetch(new URL(`/api/verity/institute/language`, this.config.verityOrigin), {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.instituteLanguage.validate(obj)) {
					return obj.language;
				}
			} else if (response.status === 404) {
				return Promise.resolve("en");
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	isLiveEnabled = async () => {
		try {
			const response = await super.fetch(new URL(`/api/verity/v1/verity/settings`, this.config.verityOrigin), {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.liveInfo.validate(obj)) {
					return obj.allowLiveProctoring;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getSettings = async () => {
		try {
			const response = await super.fetch(new URL(`/api/verity/v1/verity/settings`, this.config.verityOrigin), {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.liveInfo.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getCourseSetting = async (lmsCourseId: string) => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/portal/settings?lmsCourseId=${lmsCourseId}`, this.config.verityOrigin),
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: any = await response.json();
				// TODO: update server api to return boolean instead of string
				obj["CourseSetting-allowStopRecording"] = obj?.["CourseSetting-allowStopRecording"] === "true";
				obj["InstituteSetting-allowStopRecording"] = obj?.["InstituteSetting-allowStopRecording"] === "true";
				obj["CourseEmailSetting-courseEmailSetting"] = obj?.["CourseEmailSetting-courseEmailSetting"] === "true";
				obj["InstituteEmailSetting-instituteEmailSetting"] =
					obj?.["InstituteEmailSetting-instituteEmailSetting"] === "true";
				if (this.schemaValidators.CourseSettings.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	uploadInstituteAgreement = async ({
		instituteId,
		fileName,
		extension,
		file,
		deleteFile,
	}: {
		instituteId: number;
		fileName: string;
		extension: string;
		// @ts-ignore
		file: File;
		deleteFile?: boolean;
	}) => {
		try {
			const response = await super.fetch(
				`/api/verity/agreement/${instituteId}/${fileName}.${extension}${deleteFile ? "?delete=true" : ""}`,
				{
					method: "PUT",
					credentials: "include",
					body: file,
				},
			);
			return response;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	deleteInstituteAgreement = async ({ instituteId }: { instituteId: number }) => {
		try {
			const response = await super.fetch(`/api/verity/agreement/${instituteId}/delete`, {
				method: "PUT",
				credentials: "include",
			});
			return response;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getInstituteDisabledProctoringOptions = async () => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/institute-settings/disabled-options`, this.config.verityOrigin),
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.instituteDisabledProctoringOptions.validate(obj)) {
					const ret: Array<
						TypeFromSchemaValidator<VerityApiClient["institute"]["schemaValidators"]["proctoringOptionKey"]>
					> = [];
					obj.forEach((x) => this.schemaValidators.proctoringOptionKey.validate(x) && ret.push(x));
					return ret;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	saveInstituteDisabledProctoringOptions = async (
		disabledProctoringOptions: Array<keyof ApiClientProctoringOptionV2>,
	) => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/institute-settings/disabled-options`, this.config.verityOrigin),
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ disabledProctoringOptions }),
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.instituteDisabledProctoringOptions.validate(obj)) {
					const ret: Array<
						TypeFromSchemaValidator<VerityApiClient["institute"]["schemaValidators"]["proctoringOptionKey"]>
					> = [];
					obj.forEach((x) => this.schemaValidators.proctoringOptionKey.validate(x) && ret.push(x));
					return ret;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getInstituteThirdPartyPlatforms = async () => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/institute-settings/third-party-integrations`, this.config.verityOrigin),
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();

				const selectedPlatformRoster = new Map<number, ThirdPartyPlatform>();
				const unselectedPlatformRoster = new Map<number, ThirdPartyPlatform>();

				if (this.schemaValidators.thirdPartyIntegrations.validate(obj)) {
					obj.selectedPlatforms.forEach((platform) => {
						selectedPlatformRoster.set(platform.platformId, platform);
					});
					obj.unselectedPlatforms.forEach((platform) => {
						unselectedPlatformRoster.set(platform.platformId, platform);
					});
				}

				return {
					selectedPlatformRoster,
					unselectedPlatformRoster,
				};
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	saveInstituteThirdPartyPlatforms = async (thirdPartyPlatformRosters?: ThirdPartyPlatformRosters) => {
		try {
			if (!thirdPartyPlatformRosters) {
				return { ok: false, reason: "Failed to save third party integrations." };
			}

			const selectedPlatforms = Array.from(thirdPartyPlatformRosters.selectedPlatformRoster.values()).map(
				(platform) => ({
					platformId: platform.platformId,
					platformName: platform.platformName,
				}),
			);

			const unselectedPlatforms = Array.from(thirdPartyPlatformRosters.unselectedPlatformRoster.values()).map(
				(platform) => ({
					platformId: platform.platformId,
					platformName: platform.platformName,
				}),
			);

			const serializableRosters = {
				unselectedPlatforms,
				selectedPlatforms,
			};

			const response = await super.fetch(
				new URL(`/api/verity/institute-settings/third-party-integrations`, this.config.verityOrigin),
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ thirdPartyPlatformRosters: serializableRosters }),
					credentials: "include",
				},
			);
			if (response.ok) {
				return { ok: true };
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getInstituteAllowStrictMode = async () => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/institute-settings/allow-strict-mode`, this.config.verityOrigin),
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.instituteAllowStrictMode.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	saveInstituteAllowStrictMode = async (allowStrictMode: boolean) => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/institute-settings/allow-strict-mode`, this.config.verityOrigin),
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ allowStrictMode }),
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.instituteAllowStrictMode.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getInstituteScopeSettings = async () => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/institute-settings/scope-mode`, this.config.verityOrigin),
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.instituteScopeMode.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	saveInstituteScopeSettings = async (scopeSettingsData: {
		scopeMode: "allCourses" | "selectedAccounts" | "selectedCourses";
		enabledAccounts: number[];
		enabledCourses: string[];
	}) => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/institute-settings/scope-mode`, this.config.verityOrigin),
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ scopeSettingsData }),
					credentials: "include",
				},
			);
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getInstituteRules = async (instituteUuid: string) =>
		handleApiErrorWithSchema(
			super.fetch(new URL(`/api/verity/rule/${instituteUuid}`, this.config.verityOrigin), {
				credentials: "include",
			}),
			this.schemaValidators.rulesResponse,
		);

	updateInstituteRules = async ({
		instituteUuid,
		...rules
	}: {
		instituteUuid: string;
		type: InstituteRule["type"];
		rules: Omit<InstituteRule, "type">[];
	}) =>
		handleApiError(
			super.fetch(new URL(`/api/verity/rule/${instituteUuid}`, this.config.verityOrigin), {
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				credentials: "include",
				body: JSON.stringify(rules),
			}),
		);

	syncLmsAccounts = async () => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/institute-settings/sync-accounts`, this.config.verityOrigin),
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getInstituteIdFromUuid = async (uuid: string) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/verity/institute/uuid:${uuid}`, {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.instituteId.validate(obj)) {
					return obj.id;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getAccessControlInfo = async (uuid: string) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/institute/${uuid}`, {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.accessControlInstitute.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	updateAccessControlInfo = async ({
		instituteUuid,
		origin,
	}: {
		instituteUuid: string;
		name?: string | null;
		origin?: string | null;
		platform?: string | null;
	}) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/institute/${instituteUuid}`, {
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					// name,
					origin,
					// platform,
				}),
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.accessControlInstitute.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getApiKeys = async (uuid: string) => {
		try {
			const response = await super.fetch(`${this.config.verityOrigin}/api/access-control/institute/${uuid}/api-key`, {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.apiKeys.validate(obj)) {
					return obj.apiKeys;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	createApiKey = async ({
		instituteUuid,
		keyId,
		keyValue,
		platform,
		origin,
		apiUrl,
	}: {
		instituteUuid: string;
		keyId?: string;
		keyValue: string;
		platform?: string;
		origin?: string;
		apiUrl?: string;
	}) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/institute/${instituteUuid}/api-key`,
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						keyId,
						keyValue,
						platform,
						origin,
						apiUrl,
					}),
					credentials: "include",
				},
			);
			if (response.ok) {
				return;
			} else {
				throw new Error(`${response.status} - ${response.statusText}`);
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	updateApiKey = async ({
		instituteUuid,
		id,
		platform,
		origin,
		apiUrl,
	}: {
		instituteUuid: string;
		id: number;
		platform?: string;
		origin?: string;
		apiUrl?: string;
	}) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/institute/${instituteUuid}/api-key/${id}`,
				{
					method: "PATCH",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						platform,
						origin,
						apiUrl,
					}),
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.apiKey.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	deleteApiKey = async ({ instituteUuid, id }: { instituteUuid: string; id: number }) => {
		try {
			const response = await super.fetch(
				`${this.config.verityOrigin}/api/access-control/institute/${instituteUuid}/api-key/${id}`,
				{
					method: "DELETE",
					credentials: "include",
				},
			);
			if (response.ok) {
				return;
			} else {
				throw new Error(`${response.status} - ${response.statusText}`);
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	logInstituteUpdate = async ({
		type,
		payload,
		details,
		timestamp,
	}: {
		type: string;
		payload?: Record<string, JSONValue>;
		details?: Record<string, JSONValue>;
		timestamp?: Date;
	}) => {
		try {
			await super.fetch(`${this.config.verityOrigin}/api/verity/debug/institute`, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					details: {
						type,
						payload,
						...details,
					},
					timestamp,
				}),
			});
			return;
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getSuffix = async () => {
		try {
			const response = await super.fetch(new URL(`/api/verity/institute-settings/suffix`, this.config.verityOrigin), {
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.instituteSuffix.validate(obj)) {
					return obj.suffix;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	saveSuffix = async (suffix: string | null) => {
		try {
			const response = await super.fetch(new URL(`/api/verity/institute-settings/suffix`, this.config.verityOrigin), {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ suffix }),
				credentials: "include",
			});
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.instituteSuffix.validate(obj)) {
					return obj.suffix;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	isVerityEnabled = async (courseId: string | undefined) => {
		return (
			await handleApiErrorWithSchema(
				super.fetch(new URL(`/api/verity/institute-settings/is-verity-enabled/${courseId}`, this.config.verityOrigin), {
					credentials: "include",
				}),
				this.schemaValidators.verityEnabled,
			)
		).verityEnabled;
	};

	//#region Hostname

	/**
	 * Retrieves the list of hostnames associated with the given institute.
	 *
	 * @param {string} instituteUuid - The Database ID of the institute.
	 *
	 * @returns {Promise<string[]>} - A promise that resolves with an array of hostnames.
	 */
	getHostnames = async ({ instituteUuid }: { instituteUuid: string }) => {
		try {
			const response = await super.fetch(
				new URL(`/api/access-control/institute/${instituteUuid}/hostname`, this.config.verityOrigin),
				{
					credentials: "include",
				},
			);

			if (!response.ok) {
				throw new ApiClientError(Error(`Invalid response: ${response.statusText}`));
			}

			const obj: unknown = await response.json();

			const isValidObject = this.schemaValidators.hostnames.validate(obj);
			if (!isValidObject) {
				throw new ApiClientError(Error(`Invalid object: ${JSON.stringify(obj)}`));
			}

			return obj;
		} catch (error) {
			throw new ApiClientError(Error(`Caught Error: ${String(error)}`));
		}
	};

	/**
	 * Adds a new hostname to the given institute.
	 *
	 * @param {string} instituteUuid - The Database ID of the institute.
	 * @param {string} hostname - The hostname to be added.
	 *
	 * @returns {Promise<string>} - A promise that resolves when the hostname is successfully added.
	 */
	addHostname = async ({ instituteUuid, hostname }: { instituteUuid: string; hostname: string }) => {
		try {
			const response = await super.fetch(
				new URL(`/api/access-control/institute/${instituteUuid}/hostname/${hostname}`, this.config.verityOrigin),
				{
					method: "POST",
					credentials: "include",
				},
			);
			if (!response.ok) {
				throw new ApiClientError(Error(`Invalid response: ${response.statusText}`));
			}

			return hostname;
		} catch (error) {
			throw new ApiClientError(Error(`Caught Error: ${String(error)}`));
		}
	};

	/**
	 * Removes a hostname from the given institute.
	 *
	 * @param {number} instituteUuid - The Database ID of the institute.
	 * @param {string} hostname - The hostname to be removed.
	 *
	 * @returns {Promise<string>} - A promise that resolves when the hostname is successfully removed.
	 */
	removeHostname = async ({ instituteUuid, hostname }: { instituteUuid: string; hostname: string }) => {
		try {
			const response = await super.fetch(
				new URL(`/api/access-control/institute/${instituteUuid}/hostname/${hostname}`, this.config.verityOrigin),
				{
					method: "DELETE",
					credentials: "include",
				},
			);
			if (!response.ok) {
				throw new ApiClientError(Error(`Invalid response: ${response.statusText}`));
			}

			return hostname;
		} catch (error) {
			throw new ApiClientError(Error(`Caught Error: ${String(error)}`));
		}
	};

	getInstitutionProctoringAccommodation = async () => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/institute-settings/proctoring-accommodations`, this.config.verityOrigin),
				{
					credentials: "include",
				},
			);
			if (response.ok) {
				const obj: unknown = await response.json();
				if (this.schemaValidators.instituteProctoringAccommodation.validate(obj)) {
					return obj;
				}
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	updateInstitutionProctoringAccommodation = async (
		proctoringAccommodations: {
			userId: number;
			accommodationNotes?: string;
			proctoringOptionProfileId?: number;
		}[],
	) => {
		try {
			const response = await super.fetch(
				new URL(`/api/verity/institute-settings/proctoring-accommodations`, this.config.verityOrigin),
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({ proctoringAccommodations }),
					credentials: "include",
				},
			);
			if (response.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};
	//#endregion
}
